"use client";

import { Button } from "flowbite-react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import React, { useState, useRef, useEffect } from "react";
import { ImageCropModal } from '@/components/image-crop-modal';
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Camera } from 'lucide-react'
import { blobToFile, ingredientTypesArr } from "@/lib/utils";
import { useDuplicateIngredientCheck } from 'hook/ingredients/useDuplicateIngredientCheck'
import { DuplicateIngredientNotice } from '@/components/DuplicateIngredientNotice'
import { showDuplicateIngredientConfirm } from '@/components/DuplicateIngredientConfirm'

interface ExistingIngredient {
  ingredient_id: number
  name?: string | null
  type?: string | null
  illustrationImage?: string | null
  mainImage?: string | null
}

export const BusinessInfoSchema = z.object({
  ingredientName: z.string().min(1, 'Ingredient Name is required'),
  ingredientType: z.string().min(1, 'Ingredient Type is required'),
  contribute: z.enum(['yes', 'no'], { required_error: 'Contribute is required' }),
  description: z.string().min(5, 'Description is too short').optional(),
  image: z.union([
    z.custom<File>((val) => val instanceof File, {
      message: 'Invalid image file',
    }),
    z.null(),
  ]).optional(),
});

interface ProductManualProps {
  onNext?: () => void
  onBack?: () => void
  onCancel?: () => void
  setFormData: Function
}

const ProductManual = ({ onCancel, setFormData }: ProductManualProps) => {
  const fileName = `cropped-image-${Date.now()}.png`
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState('');
  const [originalFileName, setOriginalFileName] = useState(fileName);
  const duplicateRef = useRef<ExistingIngredient | null>(null);

  const initialValues = {
    ingredientName: '',
    ingredientType: '',
    contribute:  '',
    description: '',
    image: null,
  }

  const handleSubmit = (values: typeof initialValues) => {
    if (duplicateRef.current) {
      const existing = duplicateRef.current;
      showDuplicateIngredientConfirm({
        duplicate: existing,
        onUseExisting: () => handleUseExisting(existing),
      });
      return;
    }

    const ingredient = {
      choosing: null,
      cooking:null,
      cookingGuidance:null,
      date_created:null,
      description: values.description,
      //illustrationImage: URL.createObjectURL(values.image) || '',
      ingredient_id: null,
      isSeasonal: false,
      mainImage: null,
      name: values.ingredientName,
      pairing: null,
      type: values.ingredientType,
      allow_usage: values.contribute === 'yes' ? true : false,
      file: values.image
    }

    let imageUrl: string | null = null;

    if (values.image instanceof File) {
      imageUrl = URL.createObjectURL(values.image);
    }

    ingredient['illustrationImage'] = imageUrl

    setFormData((prevFormData: any) => ({
        ...prevFormData,
        ingredients: [...prevFormData.ingredients, ingredient],
    }))
    // onNext && onNext()
    onCancel && onCancel()
  }

  const handleUseExisting = (existing: {
    ingredient_id: number
    name?: string | null
    type?: string | null
    illustrationImage?: string | null
    mainImage?: string | null
  }) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      ingredients: [
        ...prevFormData.ingredients,
        {
          ingredient_id: existing.ingredient_id,
          name: existing.name,
          type: existing.type,
          illustrationImage: existing.illustrationImage,
          mainImage: existing.mainImage,
        },
      ],
    }))
    onCancel && onCancel()
  }

  return (
    <div className="px-4">
      <Formik
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(BusinessInfoSchema)}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, isSubmitting, ...formik }) => {
          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
              const fileUrl = URL?.createObjectURL(file);
              setImageToCrop(fileUrl);
              setShowCropModal(true);
              setOriginalFileName(file.name || 'cropped-image.jpg');
            }
          };

          const handleCropComplete = (croppedImage: string, croppedBlob: Blob) => {
            const fileImg = blobToFile(croppedBlob, originalFileName);
            setFieldValue('image', fileImg);
          };

          const { duplicate } = useDuplicateIngredientCheck(formik.values.ingredientName);
          useEffect(() => {
            duplicateRef.current = duplicate;
          }, [duplicate]);

          return (
            <Form className="flex flex-col gap-8 px-2 pt-4">
              <div className="flex justify-center">
                <label htmlFor="image" className="relative cursor-pointer">
                  <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-lg border bg-gray-200">
                    {formik.values.image ? (
                      <img
                        src={URL?.createObjectURL(formik.values.image)}
                        alt="Preview of selected image"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-5xl text-gray-500">+</span>
                    )}
                  </div>
                  <input
                    id="image"
                    type="file"
                    name="image"
                    className="hidden"
                    onChange={handleFileChange}
                    aria-label="Upload image"
                  />
                  <span className="absolute bottom-0 right-1 rounded-full bg-[#85C349] p-1 text-white">
                    <Camera size={25} />
                  </span>
                </label>
              </div>

              <div className="flex gap-6 xs:flex-col">
              <div className="flex flex-1 flex-col gap-2">
                <label htmlFor="ingredientName">Ingredient Name</label>
                <Field
                  type="text"
                  name="ingredientName"
                  placeholder="Enter ingredient name"
                  className="mt-1 h-14 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="ingredientName"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>

              <div className="flex  flex-1 flex-col gap-2 sm:max-w-[205px]">
                <label htmlFor="contribute">Ingredient Type</label>
                <Field
                  as="select"
                  name="ingredientType"
                  className={`h-14 w-full rounded-lg border border-[#CBD7DD] px-4 placeholder:text-base focus:border-primary focus:ring-primary ${
                    formik.values.ingredientType ? 'text-black' : 'text-gray-500'
                  }`}
                >
                  <option value="" disabled>
                    Select
                  </option>
                  {ingredientTypesArr.map((name: string, index: number) => (
                    <option key={index} value={name}>{name}</option>
                  ))}
                </Field>
                <ErrorMessage
                  name="ingredientType"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              </div>

              {duplicate && (
                <DuplicateIngredientNotice
                  duplicate={duplicate}
                  onUseExisting={() => handleUseExisting(duplicate)}
                />
              )}

              <div className="flex flex-col gap-2">
                <label htmlFor="description">Description of Ingredient</label>
                <Field
                  as="textarea"
                  name="description"
                  placeholder="Say something about this ingredient or product"
                  rows={6}
                  className="w-full resize-none rounded-lg border border-[#CBD7DD] px-4 py-2 placeholder:text-base focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                  <label className="leading-[19.36px] text-[#202020]">
                    Contribute
                  </label>
                  <Field
                    as="select"
                    name="contribute"
                    className={`h-14 w-full rounded-lg border border-[#CBD7DD]  px-4 placeholder:text-base focus:border-primary focus:ring-primary ${
                      formik.values.contribute ? 'text-black' : 'text-gray-500'
                    }`}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Field>
                  <span className="text-sm italic text-[#6B6B6B] ">
                    Would you like to contribute this product to our products
                    database for use by other farm businesses?
                  </span>
                  <ErrorMessage
                    name="contribute"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>
              <div className="mt-6 flex justify-end gap-4">
                <Button
                  color="gray"
                  onClick={onCancel}
                  className="font-normal bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="font-normal !bg-primary"
                >
                  Confirm Add
                </Button>
              </div>
              {/* Crop Modal */}
              <ImageCropModal
                image={imageToCrop}
                onCropComplete={handleCropComplete}
                open={showCropModal}
                onOpenChange={setShowCropModal}
                aspect={4 / 3}
                fileName={originalFileName}
                fileType="image/jpeg"
                quality={0.9}
                maxSizeMB={10}
                maxWidthOrHeight={1920}
                outputWidth={1600}
                outputHeight={1200}
                title="Crop Product Image"
                showGrid={true}
                cropShape="rect"
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  )
}

export default ProductManual
